import React from 'react';
import { Icon } from 'semantic-ui-react';
import { ConversationMessage, MessageVote } from '../goFormz-agent-types';
import { voteOnAgentMessage } from '../helpers-goPilot';
import {
  CodeBlock,
  FormulaBlock,
  LatexFormulaBlock,
  InlineCode,
  BoldText,
  BracketText,
  Heading,
  PlainText,
  formatPatterns,
  formatRegex
} from './formatComponents';

interface ChatMessageProps {
  sessionId: string;
  message: ConversationMessage;
  formulaSelected?: (formula: string) => void;
}

export default function ChatMessage({ sessionId, message, formulaSelected }: ChatMessageProps) {
  const [voteState, setVoteState] = React.useState<MessageVote>(message.vote);

  const vote = async (messageId: string, upVote: boolean) => {
    try {
      const newVote = upVote ? MessageVote.UpVote : MessageVote.DownVote;
      setVoteState(newVote);
      await voteOnAgentMessage(sessionId, messageId, newVote);
    } catch (error) {
      console.error('Error registering vote:', error);
      setVoteState(MessageVote.None);
    }
  };

  // Split message into parts
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Iterate over the message text using the regex
  while ((match = formatRegex.exec(message.message)) !== null) {
    if (match.index > lastIndex) {
      // Push the text between matches as plain text
      parts.push(message.message.slice(lastIndex, match.index));
    }
    parts.push(match[0]);
    lastIndex = formatRegex.lastIndex;
  }
  if (lastIndex < message.message.length) {
    parts.push(message.message.slice(lastIndex));
  }

  // Render the message content with appropriate components
  const renderPart = (part: string, index: number) => {
    if (formatPatterns.codeBlock.test(part)) {
      return <CodeBlock key={index} content={part} />;
    } else if (formatPatterns.formulaBlock.test(part)) {
      return <FormulaBlock key={index} content={part} formulaSelected={formulaSelected} />;
    } else if (formatPatterns.latexFormula.test(part)) {
      return <LatexFormulaBlock key={index} content={part} />;
    } else if (formatPatterns.inlineCode.test(part)) {
      return <InlineCode key={index} content={part} />;
    } else if (formatPatterns.boldText.test(part)) {
      return <BoldText key={index} content={part} />;
    } else if (formatPatterns.bracketText.test(part)) {
      return <BracketText key={index} content={part} />;
    } else if (formatPatterns.heading.test(part)) {
      return <Heading key={index} content={part} />;
    } else {
      return <PlainText key={index} content={part} />;
    }
  };

  return (
    <div className="ai-message-wrapper">
      <div className={`ai-message ${message.agentMessage ? 'agent-message' : 'user-message'}`}>
        <div className={`message-label ${message.agentMessage ? 'agent-label' : 'user-label'}`}>{message.agentMessage ? 'Agent' : 'You'}</div>
        <div className="message-content">{parts.map((part, index) => renderPart(part, index))}</div>
      </div>
      {message.agentMessage && message.messageId && (
        <div className="message-feedback">
          {voteState === MessageVote.None && <span className="feedback-text">Was this helpful?</span>}
          {voteState === MessageVote.UpVote && (
            <span className="feedback-text">
              <Icon className="clickable-icon" name="thumbs up" />
            </span>
          )}
          {voteState === MessageVote.DownVote && (
            <span className="feedback-text">
              <Icon className="clickable-icon" name="thumbs down" />
            </span>
          )}
          {voteState === MessageVote.None && (
            <span>
              <Icon className="clickable-icon" name="thumbs up outline" onClick={() => vote(message.messageId, true)} />
              <Icon className="clickable-icon" name="thumbs down outline" onClick={() => vote(message.messageId, false)} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
